import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Stepper, Step, StepLabel } from '@mui/material';
import { LimitedReportProvider, useLimitedReport } from './context/LimitedReportContext';
import { UploadImagesStep } from '../shared/steps';
import { ReviewBudgetStep } from '../shared/steps';
import NotesStep from '../shared/steps/NotesStep';
import { InvoiceStep } from '../shared/steps';
import { PreviewStep } from '../shared/steps';
import ReportFooter from '../shared/components/ReportFooter';
import { useAppContext } from 'common/AppContext';
import { deleteLimitedReport } from './limited-report.api';
import { useQuery } from 'react-query';
import { get } from '../../../common/axiosClient';
import { getLimitedReport } from './limited-report.api';
import { LimitedReportPhotoDTO } from './limited-report.type';
import CustomStepIcon from '../shared/components/CustomStepIcon';
import { useAuthorInfo } from '../shared/hooks/useAuthorInfo';
import { InvoiceInProgressProvider } from '../shared/context/InvoiceProgressContext';
import { useWizardNavigation, useStepCompletionStatus } from '../shared/wizard';

// Function to update photos directly (for real-time updates) - now just a no-op since we use context photos
const useUpdatePhotos = () => {
  return useCallback((newPhotos: LimitedReportPhotoDTO[]) => {
    // No-op since we now use photos from context
  }, []);
};

const STEPS = [
  { id: 'upload-images', title: 'Upload Images', component: <UploadImagesStep /> },
  { id: 'review-budget', title: 'Review Budget', component: <ReviewBudgetStep /> },
  { id: 'add-notes', title: 'Add Notes', component: <NotesStep /> },
  { id: 'invoice', title: 'Invoice', service: 'Limited Construction Monitoring', component: <InvoiceStep showExportAll /> },
  { id: 'preview', title: 'Preview', component: <PreviewStep /> },
];

const LimitedReportWizardContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, reportId, stepId } = useParams<{ projectId: string; reportId: string; stepId?: string }>();
  const { reportData } = location.state || {};
  const { setPageHeaderText, setBreadcrumbProjectLabel } = useAppContext();
  const [isAddingNewInvoice, setIsAddingNewInvoice] = useState(false);

  // Get context functions/state for saving
  const {
    reportState,
    completionStatusVersion,
    photos,
    photosLoaded,
    saveReport,
    isDirty,
    isSaving,
    refreshCompletionStatus,
    hasUnsavedPhotoChanges,
    navigationGuardRef,
    discardChanges,
  } = useLimitedReport();

  const { authorInfo, isLoading: isLoadingProfile } = useAuthorInfo();

  // Use shared step completion status hook
  const stepCompletionStatus = useStepCompletionStatus({
    photos,
    photosLoaded,
    budgetSummary: reportState.budgetSummary,
    invoices: reportState.invoices,
    completionStatusVersion,
  });

  // Use shared wizard navigation hook
  const { activeStep, setActiveStep, handleNext, handleBack, handleStepClick, handleDelete } = useWizardNavigation({
    totalSteps: STEPS.length,
    saveReport,
    isDirty,
    hasUnsavedPhotoChanges,
    isSaving,
    refreshCompletionStatus,
    deleteReport: async (reportIdToDelete: string) => {
      await deleteLimitedReport(reportIdToDelete);
      discardChanges();
    },
    reportName: reportData?.report?.name || '',
    reportId: reportData?.report?.id || '',
    projectId,
    navigationGuardRef,
  });

  // Function to update photos directly (for real-time updates) - now just a no-op since we use context photos
  const updatePhotos = useUpdatePhotos();

  useEffect(() => {
    setPageHeaderText(reportData?.report?.name);
    const projectSummary = reportData?.projectSummary;
    const projectLabel =
      projectSummary?.projectNumber && projectSummary?.projectTitle
        ? `${projectSummary.projectNumber} ${projectSummary.projectTitle}`
        : projectSummary?.projectTitle;
    setBreadcrumbProjectLabel(projectLabel);
    return () => {
      setPageHeaderText('');
      setBreadcrumbProjectLabel(undefined);
    };
  }, [reportData?.report?.name, reportData?.projectSummary, setPageHeaderText, setBreadcrumbProjectLabel]);

  const updateUrlForStep = useCallback(
    (stepIndex: number, replace = false) => {
      if (!projectId || !reportId) return;
      const step = STEPS[stepIndex];
      if (!step) return;
      const path = `/projects/${projectId}/limited-report/${reportId}/${step.id}`;
      navigate(path, { replace, state: location.state });
    },
    [navigate, projectId, reportId, location.state],
  );

  const activeStepRef = useRef(activeStep);
  activeStepRef.current = activeStep;

  useEffect(() => {
    if (!stepId) {
      updateUrlForStep(0, true);
      return;
    }
    const indexFromUrl = STEPS.findIndex(s => s.id === stepId);
    if (indexFromUrl >= 0 && indexFromUrl !== activeStepRef.current) {
      setActiveStep(indexFromUrl);
    }
  }, [stepId, setActiveStep, updateUrlForStep]);

  const handleNextWithUrl = async () => {
    const nextIndex = Math.min(activeStep + 1, STEPS.length - 1);
    const didTransition = await handleNext();
    if (didTransition) updateUrlForStep(nextIndex);
  };

  const handleBackWithUrl = async () => {
    const prevIndex = Math.max(activeStep - 1, 0);
    const didTransition = await handleBack();
    if (didTransition) updateUrlForStep(prevIndex);
  };

  const handleStepClickWithUrl = async (index: number) => {
    const didTransition = await handleStepClick(index);
    if (didTransition) updateUrlForStep(index);
  };

  if (!reportData) {
    return <Typography>Error: Report data not found! Please go back to the project page and open the report again.</Typography>;
  }

  // Create step components with the unsaved changes callback
  const createStepComponents = () => {
    return [
      <UploadImagesStep key='upload-images' onPhotosChange={updatePhotos} />,
      <ReviewBudgetStep key='review-budget' />,
      <NotesStep key='add-notes' />,
      <InvoiceStep key='invoice' service='Limited Construction Monitoring' />,
      <PreviewStep key='preview' />,
    ];
  };

  const stepComponents = createStepComponents();

  if (isLoadingProfile) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <InvoiceInProgressProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          <Box sx={{ minWidth: '220px', p: 2, borderRight: theme => `1px solid ${theme.palette.divider}` }}>
            <Stepper activeStep={activeStep} orientation='vertical'>
              {STEPS.map((step, index) => {
                const completionStatus = stepCompletionStatus[index] || { isComplete: false, hasWarning: false };

                return (
                  <Step key={step.id} completed={false}>
                    <StepLabel
                      onClick={() => handleStepClickWithUrl(index)}
                      sx={{
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        opacity: isSaving ? 0.6 : 1,
                        pointerEvents: isSaving ? 'none' : 'auto',
                        '.MuiStepLabel-label': {
                          fontWeight: index === activeStep ? 'bold' : 'normal',
                          color: index === activeStep ? 'inherit' : 'text.disabled',
                        },
                      }}
                      StepIconComponent={props => <CustomStepIcon {...props} icon={index + 1} completionStatus={completionStatus} />}
                    >
                      <Box>
                        {step.title}
                        {completionStatus.hasWarning && completionStatus.warningMessage && (
                          <Typography variant='caption' display='block' sx={{ color: 'warning.main', fontStyle: 'italic' }}>
                            {completionStatus.warningMessage}
                          </Typography>
                        )}
                      </Box>
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>

          <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto' }}>{stepComponents[activeStep]}</Box>
        </Box>
        <ReportFooter
          activeStep={activeStep}
          totalSteps={STEPS.length}
          onNext={handleNextWithUrl}
          onBack={handleBackWithUrl}
          onDelete={handleDelete}
          stepCompletionStatus={stepCompletionStatus}
          stepTitles={STEPS.map(step => step.title)}
        />
      </Box>
    </InvoiceInProgressProvider>
  );
};

const LimitedReportWizard = () => {
  const location = useLocation();
  const { projectId: routeProjectId, reportId } = useParams<{ projectId: string; reportId: string; stepId?: string }>();
  const { reportData: locationReportData } = (location.state as any) || {};

  // fetch user profile once
  type UserProfile = { firstName: string; lastName: string; signatureUrl?: string; email?: string };
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery<UserProfile>('currentUserProfile', async () => {
    const res = (await get(`/user/profile`)) as any;
    return res as UserProfile;
  });

  // Fetch report from backend only if not in location.state (e.g., on page refresh)
  const { data: fetchedReport, isLoading: isLoadingReport } = useQuery(['limitedReport', reportId], () => getLimitedReport(reportId!), {
    enabled: !!reportId && reportId !== 'new' && !locationReportData?.report,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
  });

  const authorInfo = useMemo(() => {
    if (!userProfile) return undefined;
    return {
      name: `${userProfile.firstName} ${userProfile.lastName}`.trim(),
      signatureUrl: userProfile.signatureUrl,
      email: userProfile.email,
    };
  }, [userProfile]);

  // Use location state if available, otherwise construct from fetched report
  const reportData = useMemo(() => {
    if (locationReportData) return locationReportData;
    if (fetchedReport) {
      return {
        report: fetchedReport,
        projectSummary: fetchedReport.project,
        drawSummary: undefined,
        budgetSummary: undefined,
        lineItems: undefined,
      };
    }
    return null;
  }, [locationReportData, fetchedReport]);

  const { projectSummary } = reportData || {};
  const report = reportData?.report || fetchedReport;

  const initialReportState = useMemo(
    () => ({
      projectSummary: reportData?.projectSummary || fetchedReport?.project,
      drawSummary: reportData?.drawSummary,
      budgetSummary: reportData?.budgetSummary,
      lineItems: reportData?.lineItems,
      summary: report?.summary,
      invoices: report?.invoices || [],
      author: authorInfo,
    }),
    [reportData, fetchedReport, report, authorInfo],
  );

  if (isLoadingProfile || isLoadingReport) {
    return <Typography>Loading...</Typography>;
  }

  if (!report && !reportData) {
    return <Typography>Error: Report data not found! Please go back to the project page and open the report again.</Typography>;
  }

  const reportIdValue = report?.id || reportData?.report?.id || reportId;
  const projectId = projectSummary?.projectId || fetchedReport?.project?.projectId || routeProjectId;

  if (!reportIdValue || !projectId) {
    return <Typography>Error: Missing report ID or project ID.</Typography>;
  }

  return (
    <InvoiceInProgressProvider>
      <LimitedReportProvider initialData={initialReportState} projectId={projectId} limitedReportId={reportIdValue}>
        <LimitedReportWizardContent />
      </LimitedReportProvider>
    </InvoiceInProgressProvider>
  );
};

export default LimitedReportWizard;
